// ============================================================
// Tasukuea Engine — Bundled for Neureka frontend
// All modules combined into a single file (client-side generation)
// ============================================================

// ─── Types ───────────────────────────────────────────────────

export enum DifficultyLevel {
  NEURONA = 'NEURONA',
  SINAPSIS = 'SINAPSIS',
  CEREBRO = 'CEREBRO',
  GENIO = 'GENIO',
  EINSTEIN = 'EINSTEIN',
}

export enum CellState {
  WHITE = 'WHITE',
  BLACK = 'BLACK',
}

export interface Position { row: number; col: number; }

export const ORTHOGONAL_DELTAS: readonly Position[] = [
  { row: -1, col: 0 }, { row: 1, col: 0 }, { row: 0, col: -1 }, { row: 0, col: 1 },
] as const;

export interface BlackRegion { row: number; col: number; size: number; }

export interface Cell {
  row: number; col: number;
  state: CellState;
  clue: number | null;
}

export interface Board {
  rows: number; cols: number;
  grid: Cell[][];
  regions: BlackRegion[];
}

export interface Puzzle {
  board: Board;
  solution: Board;
  metadata: { level: DifficultyLevel; seed: number; difficultyScore: number; };
}

interface DifficultyConfig {
  level: DifficultyLevel;
  boardSizes: [number, number][];
  clueRatioRange: [number, number];
  blackRatioRange: [number, number];
}

const DIFFICULTY_CONFIGS: Record<DifficultyLevel, DifficultyConfig> = {
  [DifficultyLevel.NEURONA]:  { level: DifficultyLevel.NEURONA,  boardSizes: [[5,5],[6,6]], clueRatioRange: [0.45,0.60], blackRatioRange: [0.15,0.25] },
  [DifficultyLevel.SINAPSIS]: { level: DifficultyLevel.SINAPSIS, boardSizes: [[6,6],[7,7]], clueRatioRange: [0.30,0.45], blackRatioRange: [0.15,0.28] },
  [DifficultyLevel.CEREBRO]:  { level: DifficultyLevel.CEREBRO,  boardSizes: [[7,7],[8,8]], clueRatioRange: [0.20,0.32], blackRatioRange: [0.18,0.30] },
  [DifficultyLevel.GENIO]:    { level: DifficultyLevel.GENIO,    boardSizes: [[8,8],[9,9]], clueRatioRange: [0.14,0.22], blackRatioRange: [0.18,0.32] },
  [DifficultyLevel.EINSTEIN]: { level: DifficultyLevel.EINSTEIN, boardSizes: [[9,9],[10,10]], clueRatioRange: [0.08,0.16], blackRatioRange: [0.20,0.35] },
};

// ─── Seeded PRNG ─────────────────────────────────────────────

class SeededRandom {
  private state: number;
  constructor(seed: number) {
    this.state = seed | 0;
    if (this.state === 0) this.state = 1;
  }
  next(): number {
    this.state |= 0;
    this.state = (this.state + 0x6d2b79f5) | 0;
    let t = Math.imul(this.state ^ (this.state >>> 15), 1 | this.state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  nextInt(min: number, max: number): number { return min + Math.floor(this.next() * (max - min + 1)); }
  shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }
  pick<T>(arr: readonly T[]): T { return arr[this.nextInt(0, arr.length - 1)]; }
}

// ─── Board utilities ─────────────────────────────────────────

function createEmptyBoard(rows: number, cols: number): Board {
  const grid: Cell[][] = [];
  for (let r = 0; r < rows; r++) {
    const row: Cell[] = [];
    for (let c = 0; c < cols; c++) row.push({ row: r, col: c, state: CellState.WHITE, clue: null });
    grid.push(row);
  }
  return { rows, cols, grid, regions: [] };
}

function cloneBoard(board: Board): Board {
  const grid = board.grid.map(row => row.map(cell => ({ ...cell })));
  const regions = board.regions.map(r => ({ ...r }));
  return { rows: board.rows, cols: board.cols, grid, regions };
}

function inBounds(board: Board, pos: Position): boolean {
  return pos.row >= 0 && pos.row < board.rows && pos.col >= 0 && pos.col < board.cols;
}

function getNeighbours(board: Board, pos: Position): Position[] {
  return ORTHOGONAL_DELTAS.map(d => ({ row: pos.row + d.row, col: pos.col + d.col })).filter(p => inBounds(board, p));
}

function regionCells(region: BlackRegion): Position[] {
  const cells: Position[] = [];
  for (let r = region.row; r < region.row + region.size; r++)
    for (let c = region.col; c < region.col + region.size; c++)
      cells.push({ row: r, col: c });
  return cells;
}

function regionArea(region: BlackRegion): number { return region.size * region.size; }

function regionBorder(region: BlackRegion, board: Board): Position[] {
  const members = new Set(regionCells(region).map(p => `${p.row},${p.col}`));
  const border = new Set<string>();
  for (const cell of regionCells(region))
    for (const n of getNeighbours(board, cell)) {
      const key = `${n.row},${n.col}`;
      if (!members.has(key)) border.add(key);
    }
  return [...border].map(key => { const [r, c] = key.split(',').map(Number); return { row: r, col: c }; });
}

function regionFitsInBoard(region: BlackRegion, board: Board): boolean {
  return region.row >= 0 && region.col >= 0 && region.row + region.size <= board.rows && region.col + region.size <= board.cols;
}

function placeRegion(board: Board, region: BlackRegion): Board {
  const newBoard = cloneBoard(board);
  for (const pos of regionCells(region)) {
    newBoard.grid[pos.row][pos.col].state = CellState.BLACK;
    newBoard.grid[pos.row][pos.col].clue = null;
  }
  newBoard.regions.push({ ...region });
  return newBoard;
}

function computeClueValue(board: Board, pos: Position): number {
  if (board.grid[pos.row][pos.col].state !== CellState.WHITE) return 0;
  const counted = new Set<number>();
  let total = 0;
  for (const n of getNeighbours(board, pos)) {
    if (board.grid[n.row][n.col].state === CellState.BLACK) {
      for (let i = 0; i < board.regions.length; i++) {
        if (counted.has(i)) continue;
        const region = board.regions[i];
        if (n.row >= region.row && n.row < region.row + region.size && n.col >= region.col && n.col < region.col + region.size) {
          counted.add(i);
          total += regionArea(region);
        }
      }
    }
  }
  return total;
}

function computeAllClues(board: Board): Board {
  const newBoard = cloneBoard(board);
  for (let r = 0; r < newBoard.rows; r++)
    for (let c = 0; c < newBoard.cols; c++)
      if (newBoard.grid[r][c].state === CellState.WHITE)
        newBoard.grid[r][c].clue = computeClueValue(newBoard, { row: r, col: c });
  return newBoard;
}

function isWhiteConnected(board: Board): boolean {
  let start: Position | null = null;
  let totalWhite = 0;
  for (let r = 0; r < board.rows; r++)
    for (let c = 0; c < board.cols; c++)
      if (board.grid[r][c].state === CellState.WHITE) { totalWhite++; if (!start) start = { row: r, col: c }; }
  if (totalWhite === 0 || !start) return true;
  const visited = new Set<string>();
  const queue: Position[] = [start];
  visited.add(`${start.row},${start.col}`);
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const n of getNeighbours(board, current)) {
      const key = `${n.row},${n.col}`;
      if (!visited.has(key) && board.grid[n.row][n.col].state === CellState.WHITE) { visited.add(key); queue.push(n); }
    }
  }
  return visited.size === totalWhite;
}

function regionsNotTouching(board: Board): boolean {
  for (const region of board.regions)
    for (const borderPos of regionBorder(region, board))
      if (board.grid[borderPos.row][borderPos.col].state === CellState.BLACK)
        for (const other of board.regions) {
          if (other === region) continue;
          for (const oc of regionCells(other))
            if (oc.row === borderPos.row && oc.col === borderPos.col) return false;
        }
  return true;
}

function allWhiteCellsHaveAdjacentBlack(board: Board): boolean {
  for (let r = 0; r < board.rows; r++)
    for (let c = 0; c < board.cols; c++)
      if (board.grid[r][c].state === CellState.WHITE) {
        if (!getNeighbours(board, { row: r, col: c }).some(n => board.grid[n.row][n.col].state === CellState.BLACK)) return false;
      }
  return true;
}

function isBoardValid(board: Board): boolean {
  return regionsNotTouching(board) && isWhiteConnected(board) && allWhiteCellsHaveAdjacentBlack(board);
}

// ─── Solution Generator ──────────────────────────────────────

function overlapsExistingBlack(board: Board, region: BlackRegion): boolean {
  for (const pos of regionCells(region)) if (board.grid[pos.row][pos.col].state === CellState.BLACK) return true;
  return false;
}

function wouldTouchExisting(board: Board, region: BlackRegion): boolean {
  const newCells = new Set(regionCells(region).map(p => `${p.row},${p.col}`));
  for (const pos of regionCells(region))
    for (const n of getNeighbours(board, pos))
      if (!newCells.has(`${n.row},${n.col}`) && board.grid[n.row][n.col].state === CellState.BLACK) return true;
  return false;
}

function generateCandidates(rows: number, cols: number, maxSize: number, rng: SeededRandom): BlackRegion[] {
  const candidates: BlackRegion[] = [];
  for (let size = 1; size <= maxSize; size++)
    for (let r = 0; r <= rows - size; r++)
      for (let c = 0; c <= cols - size; c++)
        candidates.push({ row: r, col: c, size });
  rng.shuffle(candidates);
  return candidates;
}

function generateSolution(level: DifficultyLevel, seed: number): Board | null {
  const config = DIFFICULTY_CONFIGS[level];
  const rng = new SeededRandom(seed);
  const [rows, cols] = rng.pick(config.boardSizes);
  const totalCells = rows * cols;
  const targetBlackRatio = config.blackRatioRange[0] + rng.next() * (config.blackRatioRange[1] - config.blackRatioRange[0]);
  const targetBlackCells = Math.round(totalCells * targetBlackRatio);
  const maxRegionSize = Math.min(3, Math.floor(Math.min(rows, cols) / 2));

  for (let attempt = 0; attempt < 50; attempt++) {
    const attemptRng = new SeededRandom(seed + attempt * 7919);
    let board = createEmptyBoard(rows, cols);
    let blackCount = 0;
    const candidates = generateCandidates(rows, cols, maxRegionSize, attemptRng);

    for (const candidate of candidates) {
      if (blackCount >= targetBlackCells) break;
      if (!regionFitsInBoard(candidate, board)) continue;
      if (overlapsExistingBlack(board, candidate)) continue;
      if (wouldTouchExisting(board, candidate)) continue;
      const newBoard = placeRegion(board, candidate);
      if (!isWhiteConnected(newBoard)) continue;
      board = newBoard;
      blackCount += candidate.size * candidate.size;
    }

    let rule4Satisfied = false;
    for (let fixAttempt = 0; fixAttempt < 10; fixAttempt++) {
      if (allWhiteCellsHaveAdjacentBlack(board)) { rule4Satisfied = true; break; }
      const offending: [number, number][] = [];
      for (let r = 0; r < board.rows; r++)
        for (let c = 0; c < board.cols; c++)
          if (board.grid[r][c].state === CellState.WHITE) {
            if (!getNeighbours(board, { row: r, col: c }).some(n => board.grid[n.row][n.col].state === CellState.BLACK))
              offending.push([r, c]);
          }
      if (offending.length === 0) { rule4Satisfied = true; break; }
      const [or, oc] = attemptRng.pick(offending);
      const neighbours = attemptRng.shuffle(getNeighbours(board, { row: or, col: oc }));
      let fixed = false;
      for (const n of neighbours) {
        if (board.grid[n.row][n.col].state === CellState.WHITE) {
          const region: BlackRegion = { row: n.row, col: n.col, size: 1 };
          if (!wouldTouchExisting(board, region)) {
            const tempBoard = placeRegion(board, region);
            if (isWhiteConnected(tempBoard)) { board = tempBoard; fixed = true; break; }
          }
        }
      }
      if (!fixed) break;
    }

    if (!rule4Satisfied) continue;
    if (isBoardValid(board)) return board;
  }
  return null;
}

function generateSolutionOrThrow(level: DifficultyLevel, seed: number): Board {
  for (let i = 0; i < 100; i++) {
    const result = generateSolution(level, seed + i * 104729);
    if (result) return result;
  }
  throw new Error(`Failed to generate solution for level ${level} with seed ${seed}`);
}

// ─── Clue Manager ────────────────────────────────────────────

function buildClues(board: Board): Board { return computeAllClues(board); }

function reduceClues(board: Board, level: DifficultyLevel, seed: number, isLogicallySolvable: (b: Board) => boolean): Board {
  const config = DIFFICULTY_CONFIGS[level];
  const rng = new SeededRandom(seed);
  let puzzle = cloneBoard(board);
  const whiteCells: [number, number][] = [];
  for (let r = 0; r < puzzle.rows; r++)
    for (let c = 0; c < puzzle.cols; c++)
      if (puzzle.grid[r][c].state === CellState.WHITE && puzzle.grid[r][c].clue !== null)
        whiteCells.push([r, c]);
  const totalWhite = whiteCells.length;
  const targetClueRatio = config.clueRatioRange[0] + rng.next() * (config.clueRatioRange[1] - config.clueRatioRange[0]);
  const targetCluesKept = Math.max(1, Math.round(totalWhite * targetClueRatio));
  rng.shuffle(whiteCells);
  let currentClueCount = totalWhite;

  // First pass: try with logical solvability check (works when solver is strong enough)
  for (const [r, c] of whiteCells) {
    if (currentClueCount <= targetCluesKept) break;
    if (puzzle.grid[r][c].clue === 0) continue;
    const savedClue = puzzle.grid[r][c].clue;
    puzzle.grid[r][c].clue = null;
    if (isLogicallySolvable(puzzle)) { currentClueCount--; }
    else { puzzle.grid[r][c].clue = savedClue; }
  }

  // Second pass: solver too weak to verify — force-remove non-zero clues to reach target
  // Keep at least one clue per non-zero value so the player has anchor points
  if (currentClueCount > targetCluesKept) {
    for (const [r, c] of whiteCells) {
      if (currentClueCount <= targetCluesKept) break;
      if (puzzle.grid[r][c].clue === null) continue; // already removed
      if (puzzle.grid[r][c].clue === 0) continue;    // keep zero clues
      puzzle.grid[r][c].clue = null;
      currentClueCount--;
    }
  }

  return puzzle;
}

// ─── Logical Solver (simplified for performance) ─────────────

enum SolveState { UNKNOWN = 'UNKNOWN', WHITE = 'WHITE', BLACK = 'BLACK' }

interface CandidateRegion2 { row: number; col: number; size: number; id: number; }
interface SolveCell2 { row: number; col: number; state: SolveState; clue: number | null; possibleRegions: Set<number>; }

export interface LogicalSolverResult {
  solved: boolean; board: Board | null; steps: number; rulesUsed: Set<string>;
}

class LogicalSolver {
  private rows: number; private cols: number;
  private grid: SolveCell2[][];
  private candidates: Map<number, CandidateRegion2>;
  private steps = 0; private rulesUsed = new Set<string>(); private changed = false;

  constructor(board: Board) {
    this.rows = board.rows; this.cols = board.cols;
    this.grid = [];
    for (let r = 0; r < this.rows; r++) {
      const row: SolveCell2[] = [];
      for (let c = 0; c < this.cols; c++)
        row.push({ row: r, col: c, state: SolveState.UNKNOWN, clue: board.grid[r][c].clue, possibleRegions: new Set() });
      this.grid.push(row);
    }
    const maxSize = Math.min(3, Math.floor(Math.min(this.rows, this.cols) / 2));
    this.candidates = new Map();
    let id = 0;
    for (let size = 1; size <= maxSize; size++)
      for (let r = 0; r <= this.rows - size; r++)
        for (let c = 0; c <= this.cols - size; c++) {
          let overlapsClue = false;
          for (let dr = 0; dr < size && !overlapsClue; dr++)
            for (let dc = 0; dc < size && !overlapsClue; dc++)
              if (board.grid[r + dr][c + dc].clue !== null) overlapsClue = true;
          if (!overlapsClue) {
            const cand: CandidateRegion2 = { row: r, col: c, size, id };
            this.candidates.set(id, cand);
            for (let dr = 0; dr < size; dr++)
              for (let dc = 0; dc < size; dc++)
                this.grid[r + dr][c + dc].possibleRegions.add(id);
            id++;
          }
        }
    // Mark clue cells as WHITE
    for (let r = 0; r < this.rows; r++)
      for (let c = 0; c < this.cols; c++)
        if (board.grid[r][c].clue !== null) this.grid[r][c].state = SolveState.WHITE;
  }

  private setCellWhite(r: number, c: number, rule: string): void {
    const cell = this.grid[r][c];
    if (cell.state === SolveState.WHITE) return;
    if (cell.state === SolveState.BLACK) throw new Error('Contradiction');
    cell.state = SolveState.WHITE; this.changed = true; this.rulesUsed.add(rule); this.steps++;
    for (const regId of cell.possibleRegions) this.eliminateCandidate(regId);
    cell.possibleRegions.clear();
  }

  private setCellBlack(r: number, c: number, rule: string): void {
    const cell = this.grid[r][c];
    if (cell.state === SolveState.BLACK) return;
    if (cell.state === SolveState.WHITE) throw new Error('Contradiction');
    cell.state = SolveState.BLACK; this.changed = true; this.rulesUsed.add(rule); this.steps++;
  }

  private eliminateCandidate(id: number): void {
    const cand = this.candidates.get(id);
    if (!cand) return;
    for (let dr = 0; dr < cand.size; dr++)
      for (let dc = 0; dc < cand.size; dc++)
        this.grid[cand.row + dr][cand.col + dc].possibleRegions.delete(id);
    this.candidates.delete(id);
  }

  private orthNeighbours(r: number, c: number): [number, number][] {
    return ORTHOGONAL_DELTAS.map(d => [r + d.row, c + d.col] as [number, number])
      .filter(([nr, nc]) => nr >= 0 && nr < this.rows && nc >= 0 && nc < this.cols);
  }

  private getCandidateCells(cand: CandidateRegion2): [number, number][] {
    const cells: [number, number][] = [];
    for (let dr = 0; dr < cand.size; dr++) for (let dc = 0; dc < cand.size; dc++) cells.push([cand.row + dr, cand.col + dc]);
    return cells;
  }

  private ruleCandidateElimination(): void {
    for (const [id, cand] of this.candidates) {
      let eliminate = false;
      for (let dr = 0; dr < cand.size && !eliminate; dr++)
        for (let dc = 0; dc < cand.size && !eliminate; dc++)
          if (this.grid[cand.row + dr][cand.col + dc].state === SolveState.WHITE) eliminate = true;
      if (!eliminate) {
        const cells = this.getCandidateCells(cand);
        const cellSet = new Set(cells.map(([r, c]) => `${r},${c}`));
        outer: for (const [cr, cc] of cells)
          for (const [nr, nc] of this.orthNeighbours(cr, cc))
            if (!cellSet.has(`${nr},${nc}`) && this.grid[nr][nc].state === SolveState.BLACK) { eliminate = true; break outer; }
      }
      if (eliminate) { this.eliminateCandidate(id); this.changed = true; }
    }
  }

  private ruleForcedWhite(): void {
    for (let r = 0; r < this.rows; r++)
      for (let c = 0; c < this.cols; c++) {
        const cell = this.grid[r][c];
        if (cell.state === SolveState.UNKNOWN && cell.possibleRegions.size === 0)
          this.setCellWhite(r, c, 'no_candidates');
      }
  }

  private ruleUniqueRegion(): void {
    for (let r = 0; r < this.rows; r++)
      for (let c = 0; c < this.cols; c++) {
        const cell = this.grid[r][c];
        if (cell.state !== SolveState.BLACK) continue;
        const containing: CandidateRegion2[] = [];
        for (const [id, cand] of this.candidates)
          if (r >= cand.row && r < cand.row + cand.size && c >= cand.col && c < cand.col + cand.size)
            containing.push(cand);
        if (containing.length === 1) {
          const cand = containing[0];
          for (const [cr, cc] of this.getCandidateCells(cand))
            if (this.grid[cr][cc].state === SolveState.UNKNOWN) this.setCellBlack(cr, cc, 'unique_region');
        }
      }
  }

  private ruleClueDeduction(): void {
    for (let r = 0; r < this.rows; r++)
      for (let c = 0; c < this.cols; c++) {
        const cell = this.grid[r][c];
        if (cell.clue === null) continue;
        const clueValue = cell.clue;
        let placedArea = 0;
        const visited = new Set<string>();
        for (const [nr, nc] of this.orthNeighbours(r, c))
          if (this.grid[nr][nc].state === SolveState.BLACK) {
            const key = `${nr},${nc}`;
            if (visited.has(key)) continue;
            const queue: [number, number][] = [[nr, nc]]; visited.add(key);
            let size = 0;
            while (queue.length > 0) {
              const [cr, cc] = queue.shift()!; size++;
              for (const [nnr, nnc] of this.orthNeighbours(cr, cc)) {
                const nkey = `${nnr},${nnc}`;
                if (!visited.has(nkey) && this.grid[nnr][nnc].state === SolveState.BLACK) { visited.add(nkey); queue.push([nnr, nnc]); }
              }
            }
            placedArea += size;
          }
        if (placedArea === clueValue)
          for (const [nr, nc] of this.orthNeighbours(r, c))
            if (this.grid[nr][nc].state === SolveState.UNKNOWN) this.setCellWhite(nr, nc, 'clue_satisfied');
      }
  }

  private ruleConnectivity(): void {
    const determined = this.countDetermined();
    if (determined < this.rows * this.cols * 0.3) return;
    for (let r = 0; r < this.rows; r++)
      for (let c = 0; c < this.cols; c++) {
        if (this.grid[r][c].state !== SolveState.UNKNOWN) continue;
        if (this.wouldDisconnect(r, c)) this.setCellWhite(r, c, 'connectivity');
      }
  }

  private wouldDisconnect(r: number, c: number): boolean {
    const whitish: [number, number][] = [];
    for (let rr = 0; rr < this.rows; rr++)
      for (let cc = 0; cc < this.cols; cc++)
        if (!(rr === r && cc === c) && this.grid[rr][cc].state !== SolveState.BLACK) whitish.push([rr, cc]);
    if (whitish.length === 0) return false;
    const visited = new Set<string>();
    const queue: [number, number][] = [whitish[0]];
    visited.add(`${whitish[0][0]},${whitish[0][1]}`);
    while (queue.length > 0) {
      const [cr, cc] = queue.shift()!;
      for (const [nr, nc] of this.orthNeighbours(cr, cc)) {
        if (nr === r && nc === c) continue;
        const key = `${nr},${nc}`;
        if (!visited.has(key) && this.grid[nr][nc].state !== SolveState.BLACK) { visited.add(key); queue.push([nr, nc]); }
      }
    }
    return visited.size < whitish.length;
  }

  private countDetermined(): number {
    let count = 0;
    for (let r = 0; r < this.rows; r++) for (let c = 0; c < this.cols; c++) if (this.grid[r][c].state !== SolveState.UNKNOWN) count++;
    return count;
  }

  private toBoard(): Board {
    const grid = this.grid.map(row => row.map(cell => ({
      row: cell.row, col: cell.col,
      state: cell.state === SolveState.BLACK ? CellState.BLACK : CellState.WHITE,
      clue: cell.clue,
    })));
    const regions: BlackRegion[] = [];
    const visited = new Set<string>();
    for (let r = 0; r < this.rows; r++)
      for (let c = 0; c < this.cols; c++) {
        const key = `${r},${c}`;
        if (visited.has(key) || this.grid[r][c].state !== SolveState.BLACK) continue;
        const component: [number, number][] = [];
        const queue: [number, number][] = [[r, c]]; visited.add(key);
        while (queue.length > 0) {
          const [cr, cc] = queue.shift()!; component.push([cr, cc]);
          for (const [nr, nc] of this.orthNeighbours(cr, cc)) {
            const nkey = `${nr},${nc}`;
            if (!visited.has(nkey) && this.grid[nr][nc].state === SolveState.BLACK) { visited.add(nkey); queue.push([nr, nc]); }
          }
        }
        const minR = Math.min(...component.map(([rr]) => rr));
        const minC = Math.min(...component.map(([, cc]) => cc));
        const size = Math.round(Math.sqrt(component.length));
        regions.push({ row: minR, col: minC, size });
      }
    return { rows: this.rows, cols: this.cols, grid, regions };
  }

  solve(): LogicalSolverResult {
    try {
      for (let iter = 0; iter < 500; iter++) {
        this.changed = false;
        this.ruleCandidateElimination();
        this.ruleForcedWhite();
        this.ruleClueDeduction();
        this.ruleUniqueRegion();
        this.ruleConnectivity();
        if (!this.changed) break;
      }
    } catch {
      return { solved: false, board: null, steps: this.steps, rulesUsed: this.rulesUsed };
    }
    const allDetermined = this.countDetermined() === this.rows * this.cols;
    return { solved: allDetermined, board: this.toBoard(), steps: this.steps, rulesUsed: this.rulesUsed };
  }
}

function solveLogically(board: Board): LogicalSolverResult {
  return new LogicalSolver(board).solve();
}

// ─── Difficulty grader ───────────────────────────────────────

function gradePuzzle(solveResult: LogicalSolverResult): { score: number } {
  let score = solveResult.steps * 10;
  const rules = Array.from(solveResult.rulesUsed);
  if (rules.includes('clue_zero')) score -= 20;
  if (rules.includes('unique_region')) score += 50;
  if (rules.includes('connectivity')) score += 150;
  return { score: Math.max(1, score) };
}

// ─── Main export ─────────────────────────────────────────────

export function generatePuzzle(level: DifficultyLevel, seed?: number): Puzzle {
  const activeSeed = seed !== undefined ? seed : Math.floor(Math.random() * 1000000000);
  const solutionBoard = generateSolutionOrThrow(level, activeSeed);
  if (!isBoardValid(solutionBoard)) throw new Error('Invalid solution board');
  const fullClueBoard = buildClues(solutionBoard);
  const puzzleBoard = reduceClues(fullClueBoard, level, activeSeed, (b) => solveLogically(b).solved);
  const finalSolve = solveLogically(puzzleBoard);
  const diffData = gradePuzzle(finalSolve);
  return {
    board: puzzleBoard,
    solution: solutionBoard,
    metadata: { level, seed: activeSeed, difficultyScore: diffData.score },
  };
}
